/* ==========================================================================
   Upsell drawer — opens after a successful add-to-cart.

   Extracted from the inline <script> block in snippets/top-drawer.liquid.
   Loaded from layout/theme.liquid. Paired with assets/component-top-drawer.css.

   Opened by:
     - assets/product-form.js (PDP add-to-cart)
     - assets/rc-continuum.js (Continuum landing page buy sheet)
   Both call `document.querySelector('top-drawer').open()`.

   Closing:
     - close()      → slides out, then fetchCart() opens the cart drawer
     - closeOnly()  → slides out only (X and Escape)
   Both settle in _finishClose(), which is driven by `transitionend` on
   .top-drawer__inner filtered to propertyName === 'transform', with a timeout
   fallback for reduced-motion (where no transition runs and the event never
   fires).
   ========================================================================== */

class TopDrawer extends HTMLElement {
  constructor() {
    super();
    this.transitionDuration = 500;
    this._triggerElement = null;
    this._keydownHandler = this._handleKeydown.bind(this);
    this._mobileQuery = window.matchMedia('(max-width: 749px)');
    this._lockedOverflow = false;
    this._closeSettled = true;
    this._closeFallbackTimer = null;
    this._upgradeWatchdog = null;
    this._currentVariantId = null;
    this._currentLine = null;
    this._outcomeFired = false;
    this._bound = false;
  }

  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.panel = this.querySelector('.top-drawer__inner');
    this.upgradeButton = this.querySelector('[data-upgrade]');
    this.errorEl = this.querySelector('[data-upgrade-error]');

    /* The underlay dismisses; it does not proceed.

       It used to be bound to close(), the same method the Proceed button calls,
       so tapping outside the panel opened the cart drawer — the opposite of
       what tapping outside a modal means. closeOnly() matches the X and Escape.
       Underlay is display:none above 749px, so this is a mobile-only path. */
    this.querySelectorAll('[data-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => {
        this._fireOutcome('upsell_dismissed', { dismiss_method: 'underlay' });
        this.closeOnly();
      });
    });

    const topCloseButton = this.querySelector('[data-top-drawer-close]');
    if (topCloseButton) {
      topCloseButton.addEventListener('click', () => {
        this._fireOutcome('upsell_dismissed', { dismiss_method: 'close_button' });
        this.closeOnly();
      });
    }

    this.panel.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'transform' && this.classList.contains('is-closing')) {
        this._finishClose();
      }
    });

    this.querySelector('.top-drawer__proceed-button').addEventListener('click', (event) => {
      event.preventDefault();
      this._fireOutcome('upsell_proceed_to_cart');
      this.close();
    });

    this.upgradeButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this._upgrade();
    });
  }

  /* ── Analytics ────────────────────────────────────────────────────────
     Published through Shopify.analytics.publish, the store's own custom-event
     channel, consumed by a custom Web Pixel in admin that forwards to GA4.

     Deliberately NOT dataLayer.push: window.dataLayer exists on this store but
     it is gtag.js's command queue, not a GTM data layer — there is no GTM
     container, so pushed event objects would be silently discarded. And the
     theme's gtag instance is configured for AW-775468375 (Google Ads) only;
     the GA4 property lives inside the Google channel's sandboxed pixel and is
     unreachable from page scope.

     Every call is guarded and swallowed: analytics must never break the cart. */
  _money(cents) {
    const n = Number(cents);
    return Number.isFinite(n) ? Number((n / 100).toFixed(2)) : 0;
  }

  /* GA4's client id, read from the storefront's own _ga cookie.

     The custom pixel that forwards these events runs in a sandboxed iframe on a
     different origin, so gtag there would otherwise mint its own client id and
     the events would land in GA4 detached from the visitor's session, source
     and purchases. Reading the cookie here — in page scope, where it actually
     lives — and forwarding it lets the pixel stitch them back together.

     Cookie format is GA1.<depth>.<id>.<timestamp>; the client id is the last
     two segments joined, NOT the whole value. Depth varies with the domain, so
     count from the end rather than assuming index 2.

     Absent or malformed (consent tooling, ad blockers, or simply the first hit
     before gtag has written it) returns null and the caller omits the field —
     a wrong client id is worse than none, because it would attribute the event
     to another visitor entirely. */
  _gaClientId() {
    try {
      const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]*)/);
      if (!match) return null;
      const parts = decodeURIComponent(match[1]).split('.');
      if (parts.length < 4) return null;
      const clientId = parts.slice(-2).join('.');
      return /^\d+\.\d+$/.test(clientId) ? clientId : null;
    } catch (e) {
      return null;
    }
  }

  _payload() {
    /* base_product prefers the cart line, which is the product actually added —
       on Continuum that differs from the one this snippet was rendered with.

       Known and accepted skew: _syncFromCart() is async, so upsell_drawer_shown
       fires before the line is back and falls back to the rendered title, while
       the later outcome events carry the corrected one. Do NOT "fix" this by
       making shown await the fetch — it is the funnel denominator and must fire
       unconditionally. base_variant_id is correct on every event; join on that. */
    const line = this._currentLine;
    const gaClientId = this._gaClientId();
    const payload = {
      offer_mode: this.getAttribute('data-offer-mode') || 'swap',
      surface: this.getAttribute('data-surface') || 'pdp',
      base_product: (line && line.product_title) || this.getAttribute('data-base-product') || '',
      base_variant_id: this._currentVariantId || this.getAttribute('data-current-id') || '',
      offer_product: this.getAttribute('data-offer-product') || '',
      offer_variant_id: this.getAttribute('data-upgrade-id') || '',
      offer_price: this._money(this.getAttribute('data-offer-price-cents')),
      offer_saving: this._money(this.getAttribute('data-offer-saving-cents')),
      market: this.getAttribute('data-market') || '',
    };

    // Only present when we could read a well-formed cookie. The pixel consumes
    // this for gtag's config and must not forward it as an event parameter —
    // GA4 rejects parameter names beginning with `ga_`.
    if (gaClientId) payload.ga_client_id = gaClientId;

    return payload;
  }

  _track(name, extra) {
    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== 'function') return;
      analytics.publish(name, Object.assign(this._payload(), extra || {}));
    } catch (e) {
      /* never let instrumentation surface to the shopper */
    }
  }

  /* One impression yields at most one outcome. close() is shared by Proceed and
     the upgrade path, so without this latch a dismissal would fire alongside an
     acceptance and inflate both rates.

     upsell_offer_failed deliberately does NOT come through here — it is
     diagnostic, not an outcome. A failed offer leaves the shopper in the drawer,
     and whatever they do next is still the real outcome worth recording. */
  _fireOutcome(name, extra) {
    if (this._outcomeFired) return;
    this._outcomeFired = true;
    this._track(name, extra);
  }

  /* ── Variant resolution ───────────────────────────────────────────────
     `data-current-id` is baked at render time from
     product.selected_or_first_available_variant. It is wrong whenever the
     shopper changed the selection after the page rendered, and on the
     Continuum page template it is wrong structurally: that section renders
     this snippet with `product: p` (the large product) while its buy sheet
     can add the small product, which is a separate product entirely — so the
     baked id points at a line that was never added, and removing it is a
     no-op that leaves the shopper paying for both.

     Resolve at open() instead, most specific source first. */
  _resolveCurrentVariantId() {
    // 1. Continuum buy sheet. Both size pickers render role="radio" buttons
    //    carrying data-rc-variant; rc-continuum.js setVariant() toggles
    //    aria-checked across all of them on selection.
    const continuum = document.querySelector('[data-rc-variant][aria-checked="true"]');
    if (continuum && continuum.dataset.rcVariant) return continuum.dataset.rcVariant;

    // 2. Standard PDP — the add-to-cart form's variant input.
    const formInput = document.querySelector(
      'product-form form [name="id"], form[action*="/cart/add"] [name="id"]'
    );
    if (formInput && formInput.value) return formInput.value;

    // 3. Whatever was baked in at render.
    return this.getAttribute('data-current-id');
  }

  /* The cart line for the variant that opened this drawer — the source of both
     the real quantity and the real product identity.

     Deliberately hits /cart.js rather than CartJS.cart: product-form.js posts
     to /cart/add.js directly, so the CartJS mirror has not seen this line yet.
     Resolves to null on any failure; every caller degrades. */
  _fetchCartLine(variantId) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) return null;
        return (
          cart.items.find((item) => String(item.variant_id || item.id) === String(variantId)) ||
          null
        );
      })
      .catch(() => null);
  }

  _syncFromCart() {
    this._currentLine = null;
    return this._fetchCartLine(this._currentVariantId).then((line) => {
      this._currentLine = line;
      if (line) this._syncConfirmationRow(line);
      return line;
    });
  }

  /* The confirmation row is server-rendered from the `product` this snippet was
     called with, which is not always the product that was added.

     sections/rc-continuum.liquid renders this snippet with `product: p` — its
     large-size product — while the buy sheet on that page can add the small
     size, which is configured as a separate product, not a variant. The row
     would then name and picture the wrong product. Nothing on that page can be
     changed to fix it there, and its [data-rc-variants] island carries only
     size labels and prices, no product identity.

     So: trust the cart. Only patches when the line disagrees with what was
     rendered, which makes it a no-op on a normal PDP (where a variant switch
     keeps the same product) and self-limiting everywhere else. */
  _syncConfirmationRow(line) {
    const renderedProductId = this.getAttribute('data-current-product-id');
    if (!renderedProductId) return;
    if (String(line.product_id) === String(renderedProductId)) return;

    const titleEl = this.querySelector('[data-confirm-title]');
    if (titleEl && line.product_title) titleEl.textContent = line.product_title;

    const imageEl = this.querySelector('[data-confirm-image]');
    const source = (line.featured_image && line.featured_image.url) || line.image;
    if (imageEl && source) {
      imageEl.src = source;
      imageEl.alt = line.product_title || '';
    }
  }

  /* ── Upgrade ──────────────────────────────────────────────────────────
     Two modes, chosen by data-offer-mode on the element (resolved in Liquid
     from {market}_upgrade_is_addon, so the mode logic lives in one place):

       swap  — the offer supersedes what was added (Face Oil -> Power Couple
               Set, which contains it). Adds the offer, zeroes the current line.
       addon — the offer complements it (Discovery Set + discounted Body Mist).
               Adds the offer, leaves the current line alone.

     One atomic POST /cart/update.js either way: in swap mode the upgrade goes
     in and the current line goes to zero in a single request.

     This replaces an add-then-remove sequence that cost three serialized round
     trips, because CartJS.Core.addItem always queues a GET /cart.js behind the
     add and Queue.process runs strictly one request at a time — so the remove
     could not start until an unused cart refetch had landed. Measured at ~1.9s
     for the swap alone; this is ~0.7s.

     Atomicity also removes the reason add-then-remove existed. There is no
     longer a window where the cart holds both lines or neither, so an
     unavailable upgrade cannot cost the shopper what they already added.

     Routed through CartJS rather than a bare fetch so the queue still emits
     cart.requestComplete, which script-free-gift.js depends on. */
  _upgrade() {
    const upgradeId = this.getAttribute('data-upgrade-id');
    const currentId = this._currentVariantId || this._resolveCurrentVariantId();
    if (!upgradeId) return;

    this._setError('');
    this.upgradeButton.classList.add('free-gift-loading');
    this.upgradeButton.setAttribute('aria-busy', 'true');
    window.displayUpgrade = true;

    // The button used to spin forever if neither callback fired.
    clearTimeout(this._upgradeWatchdog);
    this._upgradeWatchdog = setTimeout(() => {
      this._failUpgrade("That took too long — your cart hasn't changed. Please try again.");
    }, 12000);

    const isAddon = this.getAttribute('data-offer-mode') === 'addon';

    /* Quantity is a deliberate difference between the modes, not an oversight.

       Swap mirrors the current line: buy 2, upgrade, get 2 — the shopper is
       exchanging what they already chose, so the count has to survive.

       Add-on is always 1, whatever the current line holds. Buying three
       Discovery Sets should not silently add three discounted Body Mists; the
       offer is one incentive, not a per-unit entitlement.

       Add-on therefore needs no cart lookup at all, which also skips a /cart.js
       round trip on the slow path. */
    const resolveQuantity = isAddon
      ? Promise.resolve(1)
      : // open() already fetched the line; only go back to the network if that failed.
        this._currentLine && this._currentLine.quantity > 0
        ? Promise.resolve(this._currentLine.quantity)
        : this._fetchCartLine(currentId).then((line) =>
            line && line.quantity > 0 ? line.quantity : 1
          );

    resolveQuantity.then((quantity) => {
      const updates = {};
      updates[upgradeId] = quantity;
      // Swap only. In add-on mode the current line stays exactly as it is.
      // The id comparison guards the degenerate case where the offer IS the
      // current line — zeroing it would undo the add in the same request.
      if (!isAddon && currentId && String(currentId) !== String(upgradeId)) {
        updates[currentId] = 0;
      }

      CartJS.updateItemQuantitiesById(updates, {
        success: () => {
          // On success only. A click that fails is not an acceptance.
          this._fireOutcome('upsell_offer_accepted');
          this._finishUpgrade();
        },
        error: (xhr) => {
          let message = isAddon
            ? 'Sorry — this offer is unavailable right now.'
            : 'Sorry — this upgrade is unavailable right now.';
          try {
            const body = xhr && (xhr.responseJSON || JSON.parse(xhr.responseText));
            if (body && body.description) message = body.description;
          } catch (e) {
            /* keep the generic message */
          }
          this._failUpgrade(message);
        },
      });
    });
  }

  _finishUpgrade() {
    clearTimeout(this._upgradeWatchdog);
    this.upgradeButton.classList.remove('free-gift-loading');
    this.upgradeButton.removeAttribute('aria-busy');
    window.displayUpgrade = false;
    this.close();
  }

  _failUpgrade(message) {
    // Diagnostic, not an outcome — _track, not _fireOutcome. The shopper is
    // still in the drawer and may yet dismiss or proceed; that is the outcome.
    this._track('upsell_offer_failed', { error_message: message || '' });
    clearTimeout(this._upgradeWatchdog);
    this.upgradeButton.classList.remove('free-gift-loading');
    this.upgradeButton.removeAttribute('aria-busy');
    // Reset even on failure — mini-cart.js and script-free-gift.js both branch
    // on this flag, and leaving it true suppresses their behaviour for the
    // rest of the session.
    window.displayUpgrade = false;
    this._setError(message);
  }

  _setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }

  /* ── Focus trap ───────────────────────────────────────────────────────── */

  _getFocusableElements() {
    return Array.from(
      this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), ' +
          'select:not([disabled]), textarea:not([disabled]), ' +
          '[tabindex]:not([tabindex="-1"])'
      )
    );
  }

  _handleKeydown(event) {
    if (event.key === 'Escape') {
      this._fireOutcome('upsell_dismissed', { dismiss_method: 'escape' });
      this.closeOnly();
      return;
    }

    if (event.key === 'Tab') {
      const focusable = this._getFocusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /* ── Body scroll lock ─────────────────────────────────────────────────
     Only locked on mobile, where the panel and its underlay cover enough of
     the viewport to warrant it. Desktop leaves the page scrollable.

     Releasing is conditional on the cart drawer not being open. close() calls
     fetchCart(), which asynchronously opens the cart drawer and sets
     overflow: hidden itself (mini-cart.js toggleCart). This drawer's own
     transitionend then fires and used to clear it unconditionally — whichever
     landed second won, and on a fast cart response that was this one, leaving
     the page scrolling behind an open cart drawer. */
  _lockScroll() {
    if (!this._mobileQuery.matches) return;
    document.body.style.overflow = 'hidden';
    this._lockedOverflow = true;
  }

  _releaseScroll() {
    if (!this._lockedOverflow) return;
    const cartDrawer = document.querySelector('#cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('is-drawer-open')) {
      // The cart drawer owns the lock now — leave it alone.
      this._lockedOverflow = false;
      return;
    }
    document.body.style.overflow = '';
    this._lockedOverflow = false;
  }

  /* ── Open / close ─────────────────────────────────────────────────────── */

  open() {
    this._triggerElement = document.activeElement;
    this._currentVariantId = this._resolveCurrentVariantId();
    this._setError('');
    this._outcomeFired = false;
    this._syncFromCart();

    clearTimeout(this._closeFallbackTimer);
    this._closeSettled = false;

    this.classList.remove('is-closing');
    this.classList.add('is-open');
    this._lockScroll();

    // Denominator for every rate below it. Fired once the drawer is actually
    // visible, and never gated on anything that can fail.
    this._track('upsell_drawer_shown');

    setTimeout(() => {
      const focusTarget =
        this.querySelector('.top-drawer__close') ||
        this.querySelector('.top-drawer__proceed-button');
      if (focusTarget) focusTarget.focus();
    }, 100);

    document.addEventListener('keydown', this._keydownHandler);
  }

  close() {
    if (!this.classList.contains('is-open')) return;
    this._beginClose();
    fetchCart();
  }

  closeOnly() {
    if (!this.classList.contains('is-open')) return;
    this._beginClose();
  }

  _beginClose() {
    this._closeSettled = false;
    this.classList.add('is-closing');

    // transitionend does not fire when the transition is suppressed
    // (prefers-reduced-motion), and without this the drawer would never
    // finish closing.
    clearTimeout(this._closeFallbackTimer);
    this._closeFallbackTimer = setTimeout(() => {
      this._finishClose();
    }, this.transitionDuration + 100);
  }

  _finishClose() {
    if (this._closeSettled) return;
    this._closeSettled = true;
    clearTimeout(this._closeFallbackTimer);

    this.classList.remove('is-open', 'is-closing');
    this._releaseScroll();
    document.removeEventListener('keydown', this._keydownHandler);

    if (this._triggerElement && typeof this._triggerElement.focus === 'function') {
      this._triggerElement.focus();
    }
    this._triggerElement = null;
  }
}

if (!customElements.get('top-drawer')) {
  customElements.define('top-drawer', TopDrawer);
}
